// Lead Card Component

import type { Business } from '../../types';
import { cn, formatDistance, getLeadTypeLabel, getLeadTypeColor, getWebsiteStatusLabel, getWebsiteStatusColor, getCategoryIcon, getLeadScoreColor, getLeadScoreLabel } from '../../lib/utils';
import { Globe, AlertTriangle, Target, Star, MessageCircle, MapPin, Phone, Mail, ExternalLink, ArrowUpRight } from 'lucide-react';

interface LeadCardProps {
  lead: Business;
  isSelected?: boolean;
  isHovered?: boolean;
  onClick: () => void;
  compact?: boolean;
}

export function LeadCard({ lead, isSelected, isHovered, onClick, compact = false }: LeadCardProps) {
  const distance = lead.coordinates ? 
    `~${formatDistance(Math.random() * 5000)}` : '';

  if (compact) {
    return (
      <button
        onClick={onClick}
        className={cn(
          'w-full p-3 rounded-lg border transition-all duration-200 text-left flex items-start gap-3',
          'hover:bg-slate-50 hover:border-slate-300',
          isSelected && 'bg-primary-50 border-primary-200',
          isHovered && !isSelected && 'bg-slate-50 border-slate-300'
        )}
      >
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          <span className="text-lg">{getCategoryIcon(lead.category)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-medium text-slate-900 truncate">{lead.name}</h4>
            <span className={cn('badge text-xs px-2 py-0.5', getLeadTypeColor(lead.leadType))}>
              {getLeadTypeLabel(lead.leadType)}
            </span>
          </div>
          <p className="text-xs text-slate-500 truncate mt-0.5">{lead.address}</p>
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
            <span className={cn('flex items-center gap-1 px-2 py-0.5 rounded-full', getLeadScoreColor(lead.leadScore))}>
              <span className="font-medium">{lead.leadScore}</span>
            </span>
            {lead.rating && (
              <span className="flex items-center gap-1 text-amber-600">
                <Star className="w-3 h-3 fill-current" />
                {lead.rating}
              </span>
            )}
            {distance && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {distance}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  }

  return (
    <article
      onClick={onClick}
      className={cn(
        'lead-card group relative overflow-hidden',
        isSelected && 'ring-2 ring-primary-500 bg-primary-50',
        isHovered && !isSelected && 'shadow-lg'
      )}
    >
      {/* Lead Type Indicator Bar */}
      <div 
        className={cn(
          'absolute top-0 left-0 right-0 h-1',
          getLeadTypeColor(lead.leadType).replace('bg-', 'bg-').replace('text-', '')
        )}
      />

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">{getCategoryIcon(lead.category)}</span>
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-900 truncate">{lead.name}</h3>
              <p className="text-sm text-slate-500 truncate">{lead.address}</p>
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <span className={cn('badge px-2.5 py-1', getLeadTypeColor(lead.leadType))}>
              {getLeadTypeLabel(lead.leadType)}
            </span>
            <span className={cn('badge px-2.5 py-1', getWebsiteStatusColor(lead.websiteStatus))}>
              {getWebsiteStatusLabel(lead.websiteStatus)}
            </span>
          </div>
        </div>

        {/* Lead Score */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-slate-700">Lead Score</span>
            <span className={cn('text-sm font-bold', getLeadScoreColor(lead.leadScore))}>
              {lead.leadScore}/100 • {getLeadScoreLabel(lead.leadScore)}
            </span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className={cn('h-full rounded-full transition-all duration-500', getLeadScoreColor(lead.leadScore).replace('text-', 'bg-').replace('bg-', 'bg-'))}
              style={{ width: `${lead.leadScore}%` }}
            />
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {lead.rating && (
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <Star className="w-5 h-5 text-amber-500 fill-current" />
              <div>
                <p className="text-lg font-bold text-slate-900">{lead.rating}</p>
                <p className="text-xs text-slate-500">{lead.reviewCount || 0} reviews</p>
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
            <MapPin className="w-5 h-5 text-slate-400" />
            <div>
              <p className="text-sm font-medium text-slate-700">Distance</p>
              <p className="text-xs text-slate-500">{distance || 'Unknown'}</p>
            </div>
          </div>

          {lead.phone && (
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <Phone className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm font-medium text-slate-700">Phone</p>
                <p className="text-xs text-slate-500 truncate max-w-[120px]">{lead.phone}</p>
              </div>
            </div>
          )}

          {lead.email && (
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <Mail className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm font-medium text-slate-700">Email</p>
                <p className="text-xs text-slate-500 truncate max-w-[120px]">{lead.email}</p>
              </div>
            </div>
          )}
        </div>

        {/* Marketing Gaps */}
        {lead.marketingGaps.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Marketing Gaps</p>
            <div className="flex flex-wrap gap-1.5">
              {lead.marketingGaps.slice(0, 5).map((gap, i) => (
                <span key={i} className="badge badge-info text-xs">
                  {gap}
                </span>
              ))}
              {lead.marketingGaps.length > 5 && (
                <span className="badge badge-info text-xs">
                  +{lead.marketingGaps.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Social & Website */}
        <div className="flex items-center gap-2 pt-3 border-t border-slate-200/50">
          {lead.website && (
            <a
              href={lead.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="btn-ghost text-xs gap-1.5 px-3 py-1.5"
              title="Visit Website"
            >
              <Globe className="w-3.5 h-3.5" />
              <span>Website</span>
            </a>
          )}
          {lead.socialMedia?.instagram && (
            <a
              href={`https://instagram.com/${lead.socialMedia.instagram.replace('@', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="btn-ghost text-xs gap-1.5 px-3 py-1.5"
              title="Instagram"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              <span>IG</span>
            </a>
          )}
          {lead.socialMedia?.facebook && (
            <a
              href={`https://facebook.com/${lead.socialMedia.facebook}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="btn-ghost text-xs gap-1.5 px-3 py-1.5"
              title="Facebook"
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              <span>FB</span>
            </a>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            className="ml-auto btn-primary text-xs gap-1.5 px-3 py-1.5"
          >
            <ArrowUpRight className="w-3.5 h-3.5" />
            <span>View Details</span>
          </button>
        </div>
      </div>
    </article>
  );
}

export default LeadCard;